import { Col, Row } from "react-grid-system";
import {
  ChangedHistory,
  DateString,
  ValidationErrors,
} from "../../../lib/api/models";
import { useGetChangedHistory } from "../../../lib/api/queries";
import {
  convertDateStringToMonthDayYearFormat,
  formatInCentralTime,
  formatToDateStringInCentralTime,
} from "../../../lib/formatting";
import StyledButton from "../../common/buttons/StyledButton";
import DateInput from "../../common/inputs/DateInput";
import Spinner from "../../common/Spinner";
import Table from "../../common/tables/Table";
import { TableColumn } from "../../common/tables/Table/Table";
import StyledError from "../../common/StyledError";
import styles from "./CMInfoChangedHistory.module.css";
import { useState } from "react";
import { subMonths } from "date-fns/subMonths";
import { format } from "date-fns/format";
import { MIN_START_AND_END_DATE_FOR_HISTORY } from "../constants";

const strings = {
  changedHistoryErrorTitle: "Failed to load Changed History",
  search: "Search",
  startDate: "Start Date",
  endDate: "End Date",
  changeDate: "CHANGE DATE",
  source: "SOURCE",
  ssn: "SSN",
  dob: "DOB",
  mmn: "MMN",
  billingAddress: "BILLING ADDRESS",
  primaryEmail: "PRIMARY EMAIL",
  alternateEmail: "ALTERNATE EMAIL",
  poeSoi: "POE/SOI",
  school: "SCHOOL",
  contactPreference: "CONTACT PREFERENCE",
  languagePreference: "LANGUAGE PREFERENCE",
  futureStartDateErrorMessage: "Start Date cannot be in the future",
  pastStartDateErrorMessage: (date: string) =>
    `Start Date cannot be before ${date}`,
  futureEndDateErrorMessage: "End Date cannot be in the future",
  pastEndDateErrorMessage: (date: string) =>
    `End Date cannot be before ${date}`,
  startDatePastEndDate: "Start Date cannot be after End Date",
  noChangedHistory: "No change history results match current search criteria",
};

type Props = {
  dwbuid: string;
  referenceId?: string;
};

export default function CMInfoChangedHistory({ dwbuid, referenceId }: Props) {
  const today = format(new Date(), "yyyy-MM-dd").toString() as DateString;

  const [searchStartDate, setSearchStartDate] = useState(
    formatToDateStringInCentralTime(subMonths(new Date(), 6)),
  );
  const [searchEndDate, setSearchEndDate] = useState(
    formatToDateStringInCentralTime(new Date()),
  );

  const [startDate, setStartDate] = useState(searchStartDate);
  const [endDate, setEndDate] = useState(searchEndDate);

  const [validationMessages, setValidationMessages] = useState<
    ValidationErrors | undefined
  >();

  const {
    data: getChangedHistoryResponse,
    isLoading: isGetChangedHistoryLoading,
    isError: isGetChangedHistoryError,
    error: getChangedHistoryError,
    refetch: getChangedHistoryRefetch,
  } = useGetChangedHistory({
    dwbuid,
    referenceId,
    startDate: searchStartDate,
    endDate: searchEndDate,
  });

  const columns: TableColumn<ChangedHistory>[] = [
    {
      header: strings.changeDate,
      size: 2,
      alignment: "left",
      render: (row) => convertDateStringToMonthDayYearFormat(row.changedDate),
    },
    {
      header: strings.source,
      size: 2,
      alignment: "left",
      render: (row) => row.agent,
    },
    {
      header: strings.ssn,
      size: 2,
      alignment: "left",
      render: (row) => row.ssnLast4,
    },
    {
      header: strings.dob,
      size: 2,
      alignment: "left",
      render: (row) => {
        // display as is for now if formatting is YYYYMMDD
        try {
          const res = row.dateOfBirth
            ? formatInCentralTime(new Date(row.dateOfBirth), "MM/dd/yyyy")
            : null;
          return res;
        } catch {
          return row.dateOfBirth;
        }
      },
    },
    {
      header: strings.mmn,
      size: 2,
      alignment: "left",
      render: (row) => row.mothersMaidenName,
    },
    {
      header: strings.billingAddress,
      size: 2,
      alignment: "left",
      render: (row) => row.address,
    },
    {
      header: strings.primaryEmail,
      size: 2,
      alignment: "left",
      render: (row) => row.primaryEmail,
    },
    {
      header: strings.alternateEmail,
      size: 2,
      alignment: "left",
      render: (row) => row.secondaryEmail,
    },
    {
      header: strings.poeSoi,
      size: 2,
      alignment: "left",
      render: (row) => row.incomeSource,
    },
    {
      header: strings.school,
      size: 2,
      alignment: "left",
      render: (row) => row.school,
    },
    {
      header: strings.contactPreference,
      size: 2,
      alignment: "left",
      render: (row) => row.preferredContactMethod,
    },
    {
      header: strings.languagePreference,
      size: 2,
      alignment: "left",
      render: (row) => row.languagePreference,
    },
  ];
  return isGetChangedHistoryLoading ? (
    <Row>
      <Col>
        <Spinner />
      </Col>
    </Row>
  ) : isGetChangedHistoryError ? (
    <Row>
      <Col>
        <StyledError
          errorTitle={strings.changedHistoryErrorTitle}
          error={getChangedHistoryError}
          refetch={getChangedHistoryRefetch}
        />
      </Col>
    </Row>
  ) : (
    <>
      <Row>
        <Col className={styles.buttonRow}>
          <DateInput
            className={styles.dateInputContainer}
            label={strings.startDate}
            value={startDate}
            onChangeDate={(val) => {
              setStartDate(val);
              setValidationMessages((prev) => ({ ...prev, startDate: [] }));
            }}
            max={today}
            min={MIN_START_AND_END_DATE_FOR_HISTORY}
            showPopover={false}
            validationMessages={validationMessages?.startDate}
          />
          <DateInput
            className={styles.dateInputContainer}
            label={strings.endDate}
            value={endDate}
            onChangeDate={(val) => {
              setEndDate(val);
              setValidationMessages((prev) => ({ ...prev, endDate: [] }));
            }}
            max={today}
            min={MIN_START_AND_END_DATE_FOR_HISTORY}
            showPopover={false}
            validationMessages={validationMessages?.endDate}
          />
          <StyledButton
            className={styles.searchButton}
            onClick={() => {
              const tempEndDateValidation: string[] = [];
              const tempStartDateValidation: string[] = [];
              if (endDate > today) {
                tempEndDateValidation.push(strings.futureEndDateErrorMessage);
              }
              if (endDate < MIN_START_AND_END_DATE_FOR_HISTORY) {
                tempEndDateValidation.push(
                  strings.pastEndDateErrorMessage(
                    convertDateStringToMonthDayYearFormat(
                      MIN_START_AND_END_DATE_FOR_HISTORY,
                    ),
                  ),
                );
              }
              if (startDate > today) {
                tempStartDateValidation.push(
                  strings.futureStartDateErrorMessage,
                );
              }
              if (startDate < MIN_START_AND_END_DATE_FOR_HISTORY) {
                tempStartDateValidation.push(
                  strings.pastStartDateErrorMessage(
                    convertDateStringToMonthDayYearFormat(
                      MIN_START_AND_END_DATE_FOR_HISTORY,
                    ),
                  ),
                );
              }
              if (startDate > endDate) {
                tempStartDateValidation.push(strings.startDatePastEndDate);
              }

              if (
                tempEndDateValidation.length ||
                tempStartDateValidation.length
              ) {
                setValidationMessages({
                  endDate: tempEndDateValidation,
                  startDate: tempStartDateValidation,
                });
              } else {
                setValidationMessages(undefined);
                setSearchStartDate(startDate);
                setSearchEndDate(endDate);
              }
            }}
          >
            {strings.search}
          </StyledButton>
        </Col>
      </Row>
      <Row>
        <Col>
          <Table
            className={styles.table}
            columns={columns}
            data={getChangedHistoryResponse.results}
            emptyTableMessage={strings.noChangedHistory}
          />
        </Col>
      </Row>
    </>
  );
}
